import React, {useEffect, useRef, useState} from "react";
import SimmsTextArea from "../../../../../../components/input/SimmsTextArea";
import {
    checkDiligenceAsset,
    getDiligence,
    updateDiligenceAssetMessage,
} from "../../../../../../api/asset/DiligenceService";
import {handleActiveError, handleActiveToast} from "../../../../../../components/sonner/ToastBasic";
import "./diligenceAssetMessageModal.css";

function DiligenceAssetQuickHandleModal({open, onClose, diligenceId, asset, onProcessed}) {
    const [draft, setDraft] = useState("");
    const [diligenceTitle, setDiligenceTitle] = useState("");
    const [saving, setSaving] = useState(false);
    const [processing, setProcessing] = useState(false);
    // 로딩 응답이 도착하기 전에 사용자가 이미 입력을 시작했다면 덮어쓰지 않기 위한 플래그
    const editedRef = useRef(false);

    // 모달이 열릴 때 기존 실사내용/실사 제목을 불러옴 (자산이 여러 실사에 동시에 걸려있을 수 있어 어느 실사인지 구분할 수 있도록 표시)
    useEffect(() => {
        if (!open || !asset || diligenceId == null) {
            return undefined;
        }

        let cancelled = false;
        editedRef.current = false;
        setDraft("");
        setDiligenceTitle("");

        getDiligence(diligenceId)
            .then((response) => {
                if (cancelled || response.status !== 200) {
                    return;
                }
                setDiligenceTitle(response.data?.title ?? "");
                const matched = (response.data?.assets ?? []).find(
                    (each) => each.asset?.id === asset.id,
                );
                if (!editedRef.current) {
                    setDraft(matched?.message ?? "");
                }
            })
            .catch((err) => {
                console.error("실사내용 조회 실패:", err);
            });

        return () => {
            cancelled = true;
        };
    }, [open, asset, diligenceId]);

    useEffect(() => {
        if (!open) return undefined;

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (e) => {
            if (e.key === "Escape") {
                onClose?.();
            }
        };
        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open, onClose]);

    if (!open || !asset) {
        return null;
    }

    const busy = saving || processing;

    const handleDraftChange = (value) => {
        editedRef.current = true;
        setDraft(value);
    };

    const handleSave = async () => {
        if (busy) {
            return;
        }

        if (draft === "") {
            handleActiveError({message: "저장 실패", description: "내용을 입력해주세요."});
            return;
        }

        setSaving(true);
        try {
            const response = await updateDiligenceAssetMessage(diligenceId, asset.id, draft);
            if (response.status === 200) {
                handleActiveToast({message: "저장 되었습니다."});
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            } else {
                handleActiveError({message: "처리실패", description: "알 수 없는 원인"});
            }
        } finally {
            setSaving(false);
        }
    };

    const handleProcess = async () => {
        if (busy) {
            return;
        }

        if (!window.confirm("완료하면 수정할 수 없습니다.")) {
            return;
        }

        setProcessing(true);
        try {
            const response = await checkDiligenceAsset(diligenceId, asset.id, draft);
            if (response.status === 200) {
                handleActiveToast({message: "저장되었습니다."});
                onProcessed?.();
                onClose?.();
            } else if (response.status === 201 || response.status === 202) {
                handleActiveError({message: response.data.title, description: response.data.reason});
            } else {
                handleActiveError({message: "처리실패", description: "알 수 없는 원인"});
            }
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="diligence-message-modal-root">
            <div className="diligence-message-modal-overlay" onClick={onClose} aria-hidden />
            <div
                className="diligence-message-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="diligence-asset-quick-handle-modal-title"
            >
                <div className="diligence-message-modal__header">
                    <div>
                        <h3 id="diligence-asset-quick-handle-modal-title" className="diligence-message-modal__title">
                            {diligenceTitle || "실사내용"}
                        </h3>
                        <p className="diligence-message-modal__meta">{asset.assetName}</p>
                    </div>
                    <button
                        type="button"
                        className="diligence-message-modal__close"
                        onClick={onClose}
                        aria-label="닫기"
                    >
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path
                                d="M18 6L6 18M6 6l12 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>

                <div className="diligence-message-modal__body">
                    <label className="diligence-message-modal__label">실사내용</label>
                    <SimmsTextArea
                        auth
                        data={draft}
                        update={false}
                        style={{width: "100%", minHeight: "140px", margin: 0}}
                        onChange={handleDraftChange}
                    />
                </div>

                <div className="diligence-message-modal__footer">
                    <button
                        type="button"
                        className="diligence-message-modal__btn is-muted"
                        onClick={handleSave}
                        disabled={busy}
                    >
                        {saving ? "저장 중…" : "저장"}
                    </button>
                    <button
                        type="button"
                        className="diligence-message-modal__btn is-primary"
                        onClick={handleProcess}
                        disabled={busy}
                    >
                        {processing ? "처리 중…" : "실사처리"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DiligenceAssetQuickHandleModal;
